/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required */
import memoize from 'lodash/memoize';
import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
} from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import {
	type EntityATI,
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
	type ThirdPartyFilters,
} from '@atlassian/search-client';
import {
	type BackendExperiment,
	type ExperimentConfig,
	type FrontendExperiments,
	getBackendExperiment,
} from '@atlassian/search-experiment';

import { ApplicationModes } from '../../../common/constants/application-modes';
import {
	type BootstrapState,
	type ClearReferrerCallback,
	initialState,
	type OnSearchCompletedCallback,
	type RefreshSearchSessionIdCallback,
	type SetQueryParamsCallback,
	type SetThirdPartyConfigsCallback,
} from '../../../common/constants/bootstrap';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import type { NounKeys } from '../../../common/constants/nouns';
import { ProductKeys } from '../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import {
	productKeySchema,
	type SearchQueryStringParams,
	type SearchQueryStringParamsRaw,
} from '../../../common/constants/schemas/query-params';
import {
	type UserDetailsWithPicture,
	type UserDetailsWithStatus,
} from '../../../common/constants/types';
import { getApplicationMode, getUser } from '../../../common/utils/bootstrap';
import {
	getMultiSiteSearchInRovo,
	getUserShardDebounceTimeout,
	serveUserShardModel,
	serveUserShardUI,
} from '../../../common/utils/experiments';
import { isHello } from '../../../common/utils/is-hello';
import {
	areAllEqualQueryParams,
	parseQueryParams,
	toQueryParamsRaw,
	toQueryString,
} from '../../../common/utils/query-params';

import {
	type HookReturn,
	useAggAbsoluteUrlContext,
	useAggregatorHostNameContext,
	useAllFilterExcludedProductsContext,
	useAnalyticsAttributesContext,
	useApplicationModeContext,
	useAutoSelectPrimaryProductContext,
	useAvailableNounsContext,
	useAvailableProductsContext,
	useBackendExperimentContext,
	useBootstrapContext,
	useCurrentUserIdContext,
	useExperimentConfigContext,
	useExperimentLayersContext,
	useFiltersDiscoverability2EnabledContext,
	useFrontendExperimentsContext,
	useHasEditOrViewPlanPermissionContext,
	useInitialSpaceARIsContext,
	useInitialTextQueryParamContext,
	useIntercomHmacContext,
	useIsAvailableProductsLoadedContext,
	useIsConfigLoadedContext,
	useIsHelloContext,
	useIsJiraIssueIndexedContext,
	useIsNounsAvailableContext,
	useIsRovoEnabledContext,
	useIsRovoFreeContext,
	useIsTypeFilteredToIncidentContext,
	useLinkedCloudARIsContext,
	useMultiSiteSearchInRovoContext,
	useOnSearchCompletedContext,
	useProductDataRegionsContext,
	useProductEditionContext,
	useQueryParamsContext,
	useReferrerContext,
	useSAINHostNameContext,
	useSearchSessionIdContext,
	useSmartLinkUsageContext,
	useThirdPartyConfigsContext,
} from './bootstrap-context-provider/accessors';
import {
	useClearReferrerContext,
	useRefreshSearchSessionIdContext,
	useSetQueryParamsContext,
	useSetThirdPartyConfigsContext,
} from './bootstrap-context-provider/actions';
import { serveBootstrapProviderExp } from './utils';

export const actions = {
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
	 */
	setThirdPartyConfigs:
		(
			configs: Partial<ThirdPartyConfigsBootstrap>,
		): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ thirdPartyConfigs: configs });
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const setQueryParams = useSetQueryParams()`
	 */
	setQueryParams:
		(
			queryParam: Partial<SearchQueryStringParams>,
		): Action<BootstrapState, BootstrapContainerProps> =>
		({}, { queryParams, queryParamsCallback = () => {} }) => {
			const areEqualQueryParams = areAllEqualQueryParams(
				queryParam,
				parseQueryParams(queryParams ?? {}),
			);

			if (areEqualQueryParams) {
				return;
			}

			queryParamsCallback(toQueryParamsRaw(queryParam));
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 *
	 * You should not ever need to call this directly. Open a thread in #search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
	 */
	syncQueryParams:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState, getState }, { queryParams, queryParamsCallback = () => {} }) => {
			// queryParams is the query params coming from the URL or React Router in this case
			const {
				prevQueryParams, // Previously saved query params - this is to prevent infinitely updating queryParams
				queryParams: diffQueryParamsState, // The 'internal' query params state
			} = getState();

			const queryParamsURL = parseQueryParams(queryParams ?? {});

			const prevQueryParamsString = toQueryString(prevQueryParams);
			const diffQueryParamsURLString = toQueryString(queryParamsURL);
			const diffQueryParamsStateString = toQueryString(diffQueryParamsState);

			const allQueryParamsAreEqual = areAllEqualQueryParams(
				queryParamsURL,
				prevQueryParams,
				diffQueryParamsState,
			);

			if (allQueryParamsAreEqual) {
				return;
			}

			// Any changes from the url takes the highest precedence
			const hasURLQueryParams = !!queryParams;
			if (hasURLQueryParams && prevQueryParamsString !== diffQueryParamsURLString) {
				setState({ prevQueryParams: queryParamsURL, queryParams: queryParamsURL });
				return;
			}

			if (prevQueryParamsString !== diffQueryParamsStateString) {
				//  NOTE: If you are running the callback and you don't see the query params being updated,
				//  the callback might be sanitising the query parameters. This is the case for Confluence.
				queryParamsCallback(toQueryParamsRaw(diffQueryParamsState));
				setState({ prevQueryParams: diffQueryParamsState, queryParams: diffQueryParamsState });
				return;
			}
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
	 */
	refreshSearchSessionId:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			setState({ searchSessionId: uuid() });
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const clearReferrer = useClearReferrer()`
	 */
	clearReferrer:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ referrer: undefined });
		},
};

const buildProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<BootstrapState, TActions>({ initialState, actions, name: 'bootstrap' });

export const bootstrapStoreContainerActions = {
	onInit:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ getState, setState }, { initialProps, onSearchCompleted, queryParams }) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);
			setState({
				...getState(),
				...initialProps,
				...(queryParams && { queryParams: parsedQueryParams }),
				referrer: parsedQueryParams?.referrer,
				initialTextQueryParam: parsedQueryParams?.text,
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				searchSessionId: initialProps.searchSessionId || uuid(),
				onSearchCompleted,
			});
		},
	onUpdate:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState, getState, dispatch }, { initialProps, onSearchCompleted, queryParams }) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);

			const products = buildProducts(getState().products, initialProps?.products ?? []);

			setState({
				...getState(),
				...initialProps,
				onSearchCompleted,
				products,
				referrer: parsedQueryParams?.referrer,
			});

			dispatch(actions.syncQueryParams());
		},
};

export type QueryParamsCallback = (queryParams: Partial<SearchQueryStringParamsRaw>) => void;

export type BootstrapContainerProps = {
	initialProps: Partial<BootstrapState>;
	queryParams?: Record<string, any>;
	/** Called when FPS query params should be updated in the application's router. */
	queryParamsCallback?: QueryParamsCallback;
	/**
	 * @private
	 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
	 *
	 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
	 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
	 **/
	getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;
	/** Called when the Quick Find search completes successfully. */
	onSearchCompleted?: OnSearchCompletedCallback;
};

export const BootstrapStoreContainer = createContainer<
	BootstrapState,
	TActions,
	BootstrapContainerProps
>(Store, bootstrapStoreContainerActions);

// Hooks

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-action accessors from this file if they are present
 *
 * e.g. const { setQueryParams } = useBootstrapActions();
 * -> const setQueryParams = useSetQueryParams();
 */
export const useBootstrapActions = createActionsHook(Store);

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors from this file, including for actions if they are present
 *
 * e.g. const [{ useCloudId, useQueryParams }, { useSetQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 * -> const setQueryParams = useSetQueryParams();
 */
/**
 * @deprecated Use individual accessor hooks instead (e.g. useCloudId, useThirdPartyConfigs, useAvailableProducts, etc.)
 * This monolithic hook causes unnecessary re-renders. See QS-9283.
 */
const useBootstrapStore = createHook(Store);

export const useBootstrap = (): HookReturn<BootstrapState> => {
	const serveContext = serveBootstrapProviderExp();

	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBootstrapContext()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBootstrapStore()[0]] satisfies HookReturn<BootstrapState>);
};

/**
 * @deprecated
 * See useBootstrapState in bootstrap/accessors/index.tsx
 */
export const useBootstrapStateStore = createStateHook(Store, {
	selector: (state: BootstrapState, cb: (state: BootstrapState) => unknown) => cb(state),
}) as unknown as <T>(cb: (state: BootstrapState) => T) => T;

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAnalyticsAttributes in bootstrap/index.tsx
 *
 * This hook gets the extra analytics attributes that are passed in as a prop from the consumer.
 * It should not be confused with the hooks for firing analytics events in `controllers/use-analytics`
 */
export const useAnalyticsAttributesStore = createHook(Store, {
	selector: (state) => state.analyticsAttributes,
});

/**
 * Retrieve analytics attributes from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAnalyticsAttributes = (): HookReturn<Record<string, any> | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAnalyticsAttributesContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAnalyticsAttributesStore()[0]] satisfies HookReturn<Record<string, any> | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableProducts in bootstrap/index.tsx
 */
export const useAvailableProductsStore = createStateHook(Store, {
	selector: (state) => state.products,
});

/**
 * Retrieve available products from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableProducts = (): ProductKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableProductsContext() : useAvailableProductsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableNouns in bootstrap/index.tsx
 */
export const useAvailableNounsStore = createStateHook(Store, { selector: (state) => state.nouns });

/**
 * Retrieve available nouns from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableNouns = (): NounKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableNounsContext() : useAvailableNounsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsAvailableProductsLoaded in bootstrap/index.tsx
 */
export const useIsAvailableProductsLoadedStore = createStateHook(Store, {
	selector: (state) => state.products && state.products.length > 0,
});

/**
 * Retrieve whether available products have been loaded from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsAvailableProductsLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsAvailableProductsLoadedContext() : useIsAvailableProductsLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBootstrapLoading in bootstrap/index.tsx
 */
export const useBootstrapLoadingStore = createHook(Store, {
	selector: (state) => state.products.length === 0,
});

/**
 * Retrieve whether bootstrap is still loading (products not yet available) from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBootstrapLoading = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[!useIsAvailableProductsLoadedContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBootstrapLoadingStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use usePrimaryProduct in bootstrap/accessors/index.tsx
 */
export const usePrimaryProductStore = createHook(Store, {
	selector: (state) => state.primaryProduct,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSearchSessionId in bootstrap/index.tsx
 */
export const useSearchSessionIdStore = createHook(Store, {
	selector: (state) => state.searchSessionId,
});

/**
 * Retrieve the search session ID from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSearchSessionId = (): HookReturn<string> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useSearchSessionIdContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useSearchSessionIdStore()[0]] satisfies HookReturn<string>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsHello in bootstrap/index.tsx
 */
export const useIsHelloStore = createHook(Store, { selector: (state) => isHello(state.cloudId) });

/**
 * Retrieve whether the current tenant is Hello from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsHello = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsHelloContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsHelloStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFeatures in bootstrap/accessors/index.tsx
 */
export const useFeaturesStore = createHook(Store, { selector: (state) => state.features });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBackendExperiment in bootstrap/index.tsx
 */
export const useBackendExperimentStore = createHook(Store, {
	selector: (state, args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }) => {
		return getBackendExperiment({
			experimentConfig: state.experimentConfig,
			entities: args.entities,
			productEdition: state.productEdition,
			isHello: isHello(state.cloudId),
			productDataRegions: state.productDataRegions,
			experience: SEARCH_PAGE,
			tenantId: state.cloudId,
			isJiraIssueIndexed: state.isJiraIssueIndexed,
			thirdPartyFilters: args.thirdPartyFilters,
		});
	},
});

/**
 * Retrieve the backend experiment from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBackendExperiment = (args: {
	entities: EntityATI[];
	thirdPartyFilters: ThirdPartyFilters;
}): HookReturn<BackendExperiment> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useBackendExperimentContext(args)]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBackendExperimentStore(args)[0]] satisfies HookReturn<BackendExperiment>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFrontendExperiments in bootstrap/index.tsx
 */
export const useFrontendExperimentsStore = createHook(Store, {
	selector: (state) => state.experimentConfig.frontendExperiments,
});

/**
 * Retrieve frontend experiments from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFrontendExperiments = (): HookReturn<FrontendExperiments> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useFrontendExperimentsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useFrontendExperimentsStore()[0]] satisfies HookReturn<FrontendExperiments>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSmartLinkUsage in bootstrap/index.tsx
 */
export const useSmartLinkUsageStore = createStateHook(Store, {
	selector: (state) => state.smartLinkUsage,
});

/**
 * Retrieve Smart Link usage data from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSmartLinkUsage = (): Record<string, number> | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useSmartLinkUsageContext() : useSmartLinkUsageStore();
};

export const useMultiSiteSearchInRovoStore = createStateHook(Store, {
	selector: (state) => getMultiSiteSearchInRovo(state.experimentConfig),
});

/**
 * Retrieve whether multi-site search in Rovo is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useMultiSiteSearchInRovo = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useMultiSiteSearchInRovoContext() : useMultiSiteSearchInRovoStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useCurrentUserId in bootstrap/index.tsx
 */
export const useCurrentUserIdStore = createStateHook(Store, {
	selector: (state): string | null | undefined => state.user?.id,
});

/**
 * Retrieve the current user ID from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useCurrentUserId = (): string | null | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useCurrentUserIdContext() : useCurrentUserIdStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUserShardModel in bootstrap/index.tsx
 */
export const useUserShardModelStore = createStateHook(Store, {
	selector: (): boolean => serveUserShardModel(),
});

/**
 * Retrieve whether user shard model is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: This hook calls a pure function with no state dependency.
 */
export const useUserShardModel = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? serveUserShardModel() : useUserShardModelStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUserShardUI in bootstrap/index.tsx
 */
export const useUserShardUIStore = createStateHook(Store, {
	selector: (): boolean => serveUserShardUI(),
});

/**
 * Retrieve whether user shard UI is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: This hook calls a pure function with no state dependency.
 */
export const useUserShardUI = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? serveUserShardUI() : useUserShardUIStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUserShardDebounceTimeout in bootstrap/index.tsx
 */
export const useUserShardDebounceTimeoutStore = createStateHook(Store, {
	selector: (): number | undefined => getUserShardDebounceTimeout(),
});

/**
 * Retrieve the user shard debounce timeout from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: This hook calls a pure function with no state dependency.
 */
export const useUserShardDebounceTimeout = (): number | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? getUserShardDebounceTimeout() : useUserShardDebounceTimeoutStore();
};

/* <<< Confluence QuickFind UserShard Search Experiments */

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useExperimentConfig in bootstrap/index.tsx
 */
export const useExperimentConfigStore = createHook(Store, {
	selector: (state) => state.experimentConfig,
});

/**
 * Retrieve the experiment config from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useExperimentConfig = (): HookReturn<ExperimentConfig> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useExperimentConfigContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useExperimentConfigStore()[0]] satisfies HookReturn<ExperimentConfig>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIntercomHmac in bootstrap/index.tsx
 */
export const useIntercomHmacStore = createHook(Store, { selector: (state) => state.intercomHmac });

/**
 * Retrieve Intercom HMAC from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIntercomHmac = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIntercomHmacContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIntercomHmacStore()[0]] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsNounsAvailable in bootstrap/index.tsx
 */
export const useIsNounsAvailableStore = createHook(Store, {
	selector: (state) => state.isNounsAvailable,
});

/**
 * @deprecated
 * https://hello.atlassian.net/wiki/x/bKRdZAE
 */
const useApplicationModeStore = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.isRovoEnabled],
		(isRovoEnabled): ApplicationModes => getApplicationMode({ isRovoEnabled }),
	),
});

/**
 * Retrieve the current application mode (Rovo or Unified).
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useApplicationMode = (): ApplicationModes => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useApplicationModeContext() : useApplicationModeStore();
};

/**
 * Retrieve linked cloud ARIs.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useLinkedCloudARIs = (): [string[] | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useLinkedCloudARIsContext()] : [useLinkedCloudARIsStore()[0]];
};

/**
 * Retrieve the initial text query param.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useInitialTextQueryParam = (): string | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useInitialTextQueryParamContext() : useInitialTextQueryParamStore();
};

/**
 * Retrieve the referrer.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useReferrer = (): [string | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useReferrerContext()] : [useReferrerStore()[0]];
};

/**
 * Retrieve whether nouns are available.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsNounsAvailable = (): [boolean] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useIsNounsAvailableContext()] : [useIsNounsAvailableStore()[0]];
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUser in bootstrap/accessors/index.tsx
 */
export const useUserStore = createHook(Store, {
	selector: (state): UserDetailsWithStatus => {
		const user = state.user satisfies UserDetailsWithPicture | undefined;
		return getUser(user);
	},
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useReferrer in bootstrap/index.tsx
 */
export const useReferrerStore = createHook(Store, { selector: (state) => state.referrer });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useInitialTextQueryParam in bootstrap/index.tsx
 */
export const useInitialTextQueryParamStore = createStateHook(Store, {
	selector: (state) => state.initialTextQueryParam,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOrgId in bootstrap/accessors/index.tsx
 */
export const useOrgIdStore = createHook(Store, { selector: (state) => state.orgId });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useCloudId in bootstrap/accessors/index.tsx
 */
export const useCloudIdStore = createHook(Store, {
	selector: createSelector([(state) => state.cloudId], (cloudId) => cloudId),
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useLinkedCloudARIs in bootstrap/index.tsx
 */
export const useLinkedCloudARIsStore = createHook(Store, {
	selector: (state) => state.linkedCloudARIs,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAutoSelectPrimaryProduct in bootstrap/index.tsx
 */
export const useAutoSelectPrimaryProductStore = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => state.products,
			(state) => state.primaryProduct,
			(state) => state.isRovoEnabled,
			(state) => state.queryParams.product,
			(state) => state.queryParams.cql,
		],
		(
			products,
			primaryProduct,
			isRovoEnabled,
			currentSelectedProduct,
			currentCql,
		): ProductKeys | null => {
			const applicationMode = getApplicationMode({ isRovoEnabled });
			const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

			const shouldAutoSelectPrimaryProduct =
				!currentSelectedProduct &&
				!currentCql &&
				products &&
				products.length > 0 &&
				applicationMode === ApplicationModes.Unified &&
				primaryProductToSelect.success &&
				primaryProductToSelect.data !== ProductKeys.Atlas;

			if (shouldAutoSelectPrimaryProduct) {
				return primaryProductToSelect.data;
			} else {
				return null;
			}
		},
	),
});

/**
 * Retrieve whether the primary product should be auto-selected from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAutoSelectPrimaryProduct = (): ProductKeys | null => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAutoSelectPrimaryProductContext() : useAutoSelectPrimaryProductStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsConfigLoaded in bootstrap/index.tsx
 */
export const useIsConfigLoadedStore = createStateHook(Store, {
	selector: createSelector([(state) => state.isConfigLoaded], (isConfigLoaded) => isConfigLoaded),
});

/**
 * Retrieve whether the bootstrap config has finished loading from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsConfigLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsConfigLoadedContext() : useIsConfigLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsRovoFree in bootstrap/index.tsx
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFreeStore = createHook(Store, {
	selector: (state) => !!state.rovoFeatures?.scas,
});

/**
 * Retrieve whether Rovo is free from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFree = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsRovoFreeContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsRovoFreeStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useProductEdition in bootstrap/index.tsx
 */
export const useProductEditionStore = createHook(Store, {
	selector: (state) => state.productEdition,
});

/**
 * Retrieve product edition from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useProductEdition = (): (ProductEdition | undefined)[] => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useProductEditionContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useProductEditionStore()[0]] satisfies (ProductEdition | undefined)[]);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsTypeFilteredToIncident in bootstrap/index.tsx
 */
export const useIsTypeFilteredToIncidentStore = createStateHook(Store, {
	selector: (state): boolean => state.queryParams?.type === TypeFilterValueKey.Incident,
});

/**
 * Retrieve whether the type filter is set to Incident from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsTypeFilteredToIncident = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsTypeFilteredToIncidentContext() : useIsTypeFilteredToIncidentStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useThirdPartyConfigs in bootstrap/index.tsx
 */
export const useThirdPartyConfigsStore = createHook(Store, {
	selector: (state) => state.thirdPartyConfigs,
});

/**
 * Retrieve third party configs from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useThirdPartyConfigs = (): HookReturn<ThirdPartyConfigsBootstrap> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useThirdPartyConfigsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useThirdPartyConfigsStore()[0]] satisfies HookReturn<ThirdPartyConfigsBootstrap>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFiltersDiscoverability2Enabled in bootstrap/index.tsx
 */
export const useFiltersDiscoverability2EnabledStore = createStateHook(Store, {
	selector: (state) => {
		const primaryProduct = state.primaryProduct;
		if (!primaryProduct) {
			return false;
		}

		return primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira;
	},
});

/**
 * Retrieve whether filters discoverability 2 is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFiltersDiscoverability2Enabled = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useFiltersDiscoverability2EnabledContext()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useFiltersDiscoverability2EnabledStore();
};

/**
 * Retrieve the callback to set query parameters into the URL (to commit a filter selection). Accepts the new parameter to set and merges with the current query parameters.
 *
 * The callback is expected to update the query parameters in the URL first, and then the URL change propagates back to FPS where it triggers a store update accordingly.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSetQueryParams = (): SetQueryParamsCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { setQueryParams: setQueryParamsStore } = useBootstrapActions();
	const setQueryParamsContext = useSetQueryParamsContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			setQueryParamsContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			setQueryParamsStore;
};

/**
 * Retrieve the callback to refresh the search session ID. This is used when we want to generate a new search session ID without remounting the entire bootstrap provider, such as when the user clicks on the "Search" button in Quick Find. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useRefreshSearchSessionId = (): RefreshSearchSessionIdCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { refreshSearchSessionId: refreshSearchSessionIdStore } = useBootstrapActions();
	const refreshSearchSessionIdContext = useRefreshSearchSessionIdContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			refreshSearchSessionIdContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			refreshSearchSessionIdStore;
};

export const useClearReferrer = (): ClearReferrerCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { clearReferrer: clearReferrerStore } = useBootstrapActions();
	const clearReferrerContext = useClearReferrerContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			clearReferrerContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			clearReferrerStore;
};

/**
 * Retrieve the callback to set third party configs. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSetThirdPartyConfigs = (): SetThirdPartyConfigsCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { setThirdPartyConfigs: setThirdPartyConfigsStore } = useBootstrapActions();
	const setThirdPartyConfigsContext = useSetThirdPartyConfigsContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			setThirdPartyConfigsContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			setThirdPartyConfigsStore;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOnSearchCompleted in bootstrap/index.tsx
 */
export const useOnSearchCompletedStore = createStateHook(Store, {
	selector: (state) => state.onSearchCompleted,
});

/**
 * Retrieve the callback to be called when a search is completed. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useOnSearchCompleted = (): OnSearchCompletedCallback => {
	const serveContext = serveBootstrapProviderExp();

	const onSearchCompletedStore = useOnSearchCompletedStore();
	const onSearchCompletedContext = useOnSearchCompletedContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			onSearchCompletedContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			(onSearchCompletedStore ?? (() => {}));
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useQueryParams in bootstrap/index.tsx
 */
export const useQueryParamsStore = createStateHook(Store, {
	selector: (state) => state.queryParams,
});

/**
 * Retrieve query params. QueryParams change as the user interacts with search (input, filters).
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useQueryParams = (): HookReturn<SearchQueryStringParams> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useQueryParamsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useQueryParamsStore()] satisfies HookReturn<SearchQueryStringParams>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAggregatorHostName in bootstrap/index.tsx
 */
export const useAggregatorHostNameStore = createStateHook(Store, {
	selector: (state) => state.aggregatorHostName,
});

/**
 * Retrieve the aggregator host name. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAggregatorHostName = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAggregatorHostNameContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAggregatorHostNameStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAggAbsoluteUrl in bootstrap/index.tsx
 */
export const useAggAbsoluteUrlStore = createStateHook(Store, {
	selector: (state) => state.aggAbsoluteUrl,
});

/**
 * Retrieve the aggregator absolute URL. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAggAbsoluteUrl = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAggAbsoluteUrlContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAggAbsoluteUrlStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAllFilterExcludedProducts in bootstrap/index.tsx
 */
export const useAllFilterExcludedProductsStore = createStateHook(Store, {
	selector: (state) => state.allFilterExcludedProducts,
});

/**
 * Retrieve all filter excluded products. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAllFilterExcludedProducts = (): HookReturn<ProductKeys[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAllFilterExcludedProductsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAllFilterExcludedProductsStore()] satisfies HookReturn<ProductKeys[]>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useProductDataRegions in bootstrap/index.tsx
 */
export const useProductDataRegionsStore = createStateHook(Store, {
	selector: (state) => state.productDataRegions,
});

/**
 * Retrieve product data regions. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useProductDataRegions = (): HookReturn<ProductDataRegions | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useProductDataRegionsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useProductDataRegionsStore()] satisfies HookReturn<ProductDataRegions | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsJiraIssueIndexed in bootstrap/index.tsx
 */
export const useIsJiraIssueIndexedStore = createStateHook(Store, {
	selector: (state) => state.isJiraIssueIndexed,
});

/**
 * Retrieve whether Jira issues are indexed. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsJiraIssueIndexed = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsJiraIssueIndexedContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsJiraIssueIndexedStore()] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useInitialSpaceARIs in bootstrap/index.tsx
 */
export const useInitialSpaceARIsStore = createStateHook(Store, {
	selector: (state) => state.initialSpaceARIs,
});

/**
 * Retrieve initial space ARIs. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useInitialSpaceARIs = (): HookReturn<string[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useInitialSpaceARIsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useInitialSpaceARIsStore()] satisfies HookReturn<string[]>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSAINHostName in bootstrap/index.tsx
 */
export const useSAINHostNameStore = createStateHook(Store, {
	selector: (state) => state.SAINHostName,
});

/**
 * Retrieve the SAIN host name. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSAINHostName = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useSAINHostNameContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useSAINHostNameStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useHasEditOrViewPlanPermission in bootstrap/index.tsx
 */
export const useHasEditOrViewPlanPermissionStore = createStateHook(Store, {
	selector: (state) => state.hasEditOrViewPlanPermission,
});

/**
 * Retrieve whether the user has edit or view plan permission. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useHasEditOrViewPlanPermission = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useHasEditOrViewPlanPermissionContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useHasEditOrViewPlanPermissionStore() ?? false] satisfies HookReturn<boolean>);
};

const useIsRovoEnabledStore = createStateHook(Store, {
	selector: (state): boolean => state.isRovoEnabled,
});

/**
 * Retrieve whether Rovo is enabled.
 */
export const useIsRovoEnabled = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsRovoEnabledContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsRovoEnabledStore()] satisfies HookReturn<boolean>);
};

const useExperimentLayersStore = createStateHook(Store, {
	selector: (state): string[] => state.experimentLayers,
});

/**
 * Retrieve experiment layers.
 */
export const useExperimentLayers = (): HookReturn<string[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useExperimentLayersContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useExperimentLayersStore()] satisfies HookReturn<string[]>);
};
